import type { SeasonOverview } from '@sea-snag/shared';
import { Button, Input } from '@sea-snag/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useCanSeeAdmin } from '@/hooks/use-can-see-admin';

import { analyticsApi } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

interface SeasonSettingsProps {
  orgPid: string;
  year: string;
  season: SeasonOverview | undefined;
  /** Label of the organisation's region, shown when no season is configured. */
  regionLabel: string;
}

/**
 * Edit the season inline, where it is displayed.
 *
 * Whether the dates are the organisation's own or its regional default is
 * stated rather than implied: only a configured season is widened by bookings
 * that overrun it, so the distinction changes what the strip above will do.
 */
export function SeasonSettings({ orgPid, year, season, regionLabel }: SeasonSettingsProps) {
  const canEdit = useCanSeeAdmin();

  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.analytics.seasonOverview(orgPid, year) });
    // The grid and cards are season-scoped too, so they must refetch.
    void qc.invalidateQueries({ queryKey: ['analytics', 'occupancy-season', orgPid] });
  };

  const save = useMutation({
    mutationFn: (body: { startDate: string; endDate: string }) =>
      analyticsApi.setSeasonDates({ year: Number(year), ...body }),
    onSuccess: () => {
      toast.success(`Season saved for ${year}`);
      setEditing(false);
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message || 'Could not save the season'),
  });

  const reset = useMutation({
    mutationFn: () => analyticsApi.clearSeasonDates(year),
    onSuccess: () => {
      toast.success(`${year} reset to the ${regionLabel} default`);
      setEditing(false);
      invalidate();
    },
    onError: () => toast.error('Could not reset the season'),
  });

  const [editing, setEditing] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  if (!season) return null;

  const configured = season.source === 'configured';

  if (!editing) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-xs">
        <span>{configured ? 'Your season' : `Using the ${regionLabel} default`}</span>
        {canEdit && (
          <button
            type="button"
            onClick={() => {
              setStart(season.startDate);
              setEnd(season.endDate);
              setEditing(true);
            }}
            className="hover:text-foreground inline-flex items-center gap-1 hover:underline"
          >
            <Pencil className="size-3" />
            Edit
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <Input
        type="date"
        value={start}
        onChange={(e) => setStart(e.target.value)}
        aria-label="Season start"
        className="h-7 w-[150px] text-xs"
      />
      <span className="text-muted-foreground">to</span>
      <Input
        type="date"
        value={end}
        onChange={(e) => setEnd(e.target.value)}
        aria-label="Season end"
        className="h-7 w-[150px] text-xs"
      />
      <Button
        size="sm"
        className="h-7 px-2 text-xs"
        disabled={save.isPending || !start || !end}
        onClick={() => save.mutate({ startDate: start, endDate: end })}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 px-2 text-xs"
        onClick={() => setEditing(false)}
      >
        Cancel
      </Button>
      {configured && (
        <Button
          size="sm"
          variant="ghost"
          className="text-muted-foreground h-7 px-2 text-xs"
          disabled={reset.isPending}
          onClick={() => reset.mutate()}
        >
          Reset to {regionLabel} default
        </Button>
      )}
    </div>
  );
}
